import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Activity, ArrowLeft, Bookmark, Loader2, BellRing } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { LiveMarketProvider } from "../context/LiveMarketContext";
import { WatchlistProvider, useWatchlist } from "../context/WatchlistContext";
import ProductCard from "../components/site/ProductCard";
import PreviewModal from "../components/site/PreviewModal";

function AlertForm({ ids }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!ids.length) {
      toast.error("Add templates to your watchlist first");
      return;
    }
    setBusy(true);
    try {
      const r = await api.watchAlerts(email, ids, window.location.origin);
      toast.success("Alerts on", { description: r.message });
      setEmail("");
    } catch {
      toast.error("Could not set up alerts");
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} data-testid="alert-form" className="mt-8 flex flex-col gap-3 border border-emerald-500/30 bg-emerald-500/5 p-5 sm:flex-row sm:items-center">
      <div className="flex items-center gap-2 text-sm text-emerald-300">
        <BellRing className="h-4 w-4" />
        <span className="font-mono text-xs uppercase tracking-wider">Email me on price drops</span>
      </div>
      <input
        data-testid="alert-email"
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="you@company.com"
        className="flex-1 border border-white/15 bg-slate-950 px-4 py-2.5 text-sm text-white outline-none transition-colors duration-200 placeholder:text-slate-500 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500"
      />
      <button data-testid="alert-submit" disabled={busy} className="flex items-center justify-center gap-2 bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-slate-950 transition-[background-color] duration-200 hover:bg-emerald-400 disabled:opacity-60">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <BellRing className="h-4 w-4" />} Enable alerts
      </button>
    </form>
  );
}

function WatchlistInner() {
  const { ids } = useWatchlist();
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    api.products({ sort: "popular" }).then(setAll).catch((err) => console.error("Failed to load watchlist products", err?.message || err)).finally(() => setLoading(false));
  }, []);

  const items = all.filter((p) => ids.includes(p.id));

  return (
    <div className="relative min-h-screen bg-slate-950 text-white terminal-grid">
      <header className="relative border-b border-white/10 bg-slate-950/80 backdrop-blur">
        <div className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-4 lg:px-10">
          <Link to="/" className="flex items-center gap-2.5">
            <img src="/logo.png" alt="Ticker Templates" className="h-8 w-auto" />
          </Link>
          <Link to="/" className="flex items-center gap-2 text-sm text-slate-300 transition-colors duration-200 hover:text-white">
            <ArrowLeft className="h-4 w-4" /> Marketplace
          </Link>
        </div>
      </header>

      <main className="relative mx-auto max-w-[1400px] px-6 py-14 lg:px-10">
        <span className="font-mono text-xs uppercase tracking-[0.2em] text-emerald-400">Your Positions</span>
        <h1 className="mt-2 font-display text-4xl font-black uppercase tracking-tight sm:text-5xl">Watchlist</h1>
        <p className="mt-3 max-w-lg text-base leading-relaxed text-slate-400">
          Templates you're tracking. We flag a live alert the moment the market price ticks down.
        </p>

        {loading ? (
          <div className="flex justify-center py-24 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : items.length === 0 ? (
          <div data-testid="watchlist-empty" className="mt-10 flex flex-col items-center border border-white/10 bg-slate-900 py-20 text-center">
            <Bookmark className="h-10 w-10 text-slate-600" />
            <p className="mt-4 text-slate-300">Your watchlist is empty.</p>
            <p className="mt-1 text-sm text-slate-500">Tap the bookmark on any template to start tracking its price.</p>
            <Link to="/" className="mt-6 bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-slate-950 transition-[background-color] duration-200 hover:bg-emerald-400">
              Browse the market
            </Link>
          </div>
        ) : (
          <>
            <AlertForm ids={ids} />
            <div data-testid="watchlist-grid" className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3">
              {items.map((p, i) => (
                <ProductCard key={p.id} product={p} index={i} onPreview={setPreview} />
              ))}
            </div>
          </>
        )}
      </main>

      <PreviewModal product={preview} onClose={() => setPreview(null)} />
    </div>
  );
}

export default function Watchlist() {
  return (
    <WatchlistProvider>
      <LiveMarketProvider>
        <WatchlistInner />
      </LiveMarketProvider>
    </WatchlistProvider>
  );
}
